import { EntityKind } from "./kinds.ts";
import { SNAPSHOT_RING } from "./limits.ts";
import type { LooksRecord, WireLook } from "./looks.ts";

/**
 * The looks a page holds, by handle (ADR-094 decision 6, "the page's side"): held exactly as a name is (ADR-067
 * decision 2, `view.ts`).
 *
 * A `looks` frame arrives before the snapshot of its tick, so its looks wait (`receiveLooks`) until that snapshot is
 * applied (`settleLooks`). A waiting look becomes the held look of its handle only when the view then holds that
 * handle as a player; one for a handle the snapshot did not spawn, or spawned as anything else, is discarded, so it
 * can never be worn by whoever takes the handle later. A later look for a handle replaces the earlier. A held look
 * is dropped when its handle leaves the view or is no longer a player, and a keyframe carries the held looks only
 * when it is fewer than `SNAPSHOT_RING` ticks after the last snapshot settled, the time a handle rests before it is
 * used again; so a reused handle never shows the earlier character's look. A snapshot that is discarded takes its
 * tick's waiting looks with it (`dropPendingLooks`), and a change of map or a reconnect forgets everything
 * (`clearHeldLooks`).
 *
 * The store copies what it keeps, since a decoder's record is reused by the next frame, and reuses its own look
 * objects, so a page makes at most a view's worth of them and a second view's worth while a full frame waits.
 */
export interface HeldLooks {
  /** The tick of the last snapshot settled; 0 before the first. */
  tick: number;
  /** The look held for each handle the view holds as a player and a look has arrived for. */
  readonly held: Map<number, WireLook>;
  /** The looks of frames whose snapshot has not been applied yet. */
  readonly pending: Map<number, WireLook>;
  /** Look objects let go, to be used again. */
  readonly spare: WireLook[];
  /** How many look objects the store has made, for tests. */
  made: number;
}

/**
 * Told of a handle whose held look changed as a snapshot settled: the look it holds now, or null when it lost the
 * one it held. The look is the store's own and is read, not kept.
 */
export type HeldLookChange = (handle: number, look: Readonly<WireLook> | null) => void;

/**
 * An empty store.
 *
 * @returns The store.
 */
export function newHeldLooks(): HeldLooks {
  return { tick: 0, held: new Map(), pending: new Map(), spare: [], made: 0 };
}

/**
 * Whether two wire looks are the same in every field.
 *
 * @param a One look.
 * @param b The other.
 * @returns True when no field differs.
 */
export function sameWireLook(a: Readonly<WireLook>, b: Readonly<WireLook>): boolean {
  return a.skin === b.skin && a.hair === b.hair && a.flags === b.flags && a.outfit === b.outfit && a.hat === b.hat && a.mainHand === b.mainHand && a.offHand === b.offHand;
}

/**
 * Copies a wire look's seven fields.
 *
 * @param from The look read.
 * @param to The look written.
 */
function copyLook(from: Readonly<WireLook>, to: WireLook): void {
  to.skin = from.skin;
  to.hair = from.hair;
  to.flags = from.flags;
  to.outfit = from.outfit;
  to.hat = from.hat;
  to.mainHand = from.mainHand;
  to.offHand = from.offHand;
}

/**
 * Takes a decoded `looks` frame: each of its characters' looks waits for the next snapshot applied. A handle named
 * twice, in one frame or in two before one snapshot, keeps the later look.
 *
 * @param looks The store.
 * @param record The decoded frame; its first `count` entries are read and copied.
 */
export function receiveLooks(looks: HeldLooks, record: Readonly<LooksRecord>): void {
  for (let index = 0; index < record.count; index += 1) {
    const entry = record.entries[index];
    if (entry === undefined) {
      return;
    }
    let waiting = looks.pending.get(entry.handle);
    if (waiting === undefined) {
      waiting = looks.spare.pop();
      if (waiting === undefined) {
        waiting = { skin: 0, hair: 0, flags: 0, outfit: 0, hat: 0, mainHand: 0, offHand: 0 };
        looks.made += 1;
      }
      looks.pending.set(entry.handle, waiting);
    }
    copyLook(entry, waiting);
  }
}

/**
 * Settles the store against the view an applied snapshot left: held looks whose handle the view no longer holds as a
 * player are dropped, every held look when the snapshot is a keyframe a ring or more after the last one settled, and
 * each waiting look is held where the view holds its handle as a player and discarded otherwise.
 *
 * @param looks The store.
 * @param tick The applied snapshot's tick.
 * @param keyframe Whether it was a keyframe.
 * @param entities The view's entities after it, by handle.
 * @param changed Told of each handle whose held look is another than before, or gone; nobody when omitted. A look
 *   that arrived again unchanged tells nothing.
 */
export function settleLooks(looks: HeldLooks, tick: number, keyframe: boolean, entities: ReadonlyMap<number, { readonly kind: number }>, changed?: HeldLookChange): void {
  const carried = !keyframe || tick - looks.tick < SNAPSHOT_RING;
  looks.tick = tick;
  looks.held.forEach((held, handle) => {
    const player = entities.get(handle)?.kind === EntityKind.Player;
    if (carried && player) {
      return;
    }
    const again = player ? looks.pending.get(handle) : undefined;
    if (again !== undefined && sameWireLook(again, held)) {
      return;
    }
    looks.held.delete(handle);
    looks.spare.push(held);
    if (again === undefined) {
      changed?.(handle, null);
    }
  });
  looks.pending.forEach((waiting, handle) => {
    if (entities.get(handle)?.kind !== EntityKind.Player) {
      looks.spare.push(waiting);
      return;
    }
    const held = looks.held.get(handle);
    if (held === undefined) {
      looks.held.set(handle, waiting);
      changed?.(handle, waiting);
      return;
    }
    if (!sameWireLook(held, waiting)) {
      copyLook(waiting, held);
      changed?.(handle, held);
    }
    looks.spare.push(waiting);
  });
  looks.pending.clear();
}

/**
 * Discards the looks that wait, when the snapshot they came for is discarded.
 *
 * @param looks The store.
 */
export function dropPendingLooks(looks: HeldLooks): void {
  looks.pending.forEach((waiting) => {
    looks.spare.push(waiting);
  });
  looks.pending.clear();
}

/**
 * Forgets every look, held or waiting, and the last tick settled: a change of map, a reconnect, a session's end.
 *
 * @param looks The store.
 */
export function clearHeldLooks(looks: HeldLooks): void {
  dropPendingLooks(looks);
  looks.held.forEach((held) => {
    looks.spare.push(held);
  });
  looks.held.clear();
  looks.tick = 0;
}

/**
 * The look held for a handle.
 *
 * @param looks The store.
 * @param handle The handle.
 * @returns The look, the store's own, or undefined when none is held.
 */
export function heldLook(looks: Readonly<HeldLooks>, handle: number): Readonly<WireLook> | undefined {
  return looks.held.get(handle);
}
